import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { trackEvent } from 'user-analytics';
import { BUS_TRACKER_BANNER_CLICKED } from 'constants/TrackEvents';
import busTrackerPlaceholder from 'images/bus-tracker-placeholder.png';
import PurchaseCompleteBannerLayout from './PurchaseCompleteBannerLayout';

/**
 * BusTrackerBanner component.
 * Render the Bus Tracker banner (visibility is controlled by PurchaseCompleteBanners).
 * @param {object} props - purchaseType, isOpenTicket, className.
 * @param {string} props.purchaseType - Purchase type (one_way/round_trip).
 * @param {boolean} props.isOpenTicket - Whether the purchase has open tickets.
 * @param {string} props.className - Additional CSS classes for the banner container.
 * @param {string} props.imageColumnSize - Image column width: compact or expanded.
 * @returns {JSX.Element} The rendered banner.
 */
function BusTrackerBanner({ purchaseType, isOpenTicket, className, imageColumnSize }) {
  const { t } = useTranslation('purchase');
  const features = useWhitelabelFeatures();
  const { brand } = useWhitelabelEnvs();
  const trackerUrl = features.BUS_TRACKER_URL;

  /**
   * Handle Bus Tracker banner click analytics.
   * @returns {void}
   */
  const handleClick = () => {
    trackEvent(BUS_TRACKER_BANNER_CLICKED, {
      'Page': 'Confirmation',
      'Placement': 'purchase_complete_banner',
      'Brand': brand,
      'Purchase Type': purchaseType,
      'Is Open Ticket': isOpenTicket,
      'URL': trackerUrl,
    });
  };

  return (
    <PurchaseCompleteBannerLayout
      className={className}
      imageSrc={busTrackerPlaceholder}
      imageAlt={t('label.bus_tracker')}
      bannerHref={trackerUrl}
      onBannerClick={handleClick}
      imageFill
      imageColumnSize={imageColumnSize}
    >
      <Text color="primary" weight="semibold">
        {t('label.bus_tracker_title')}
      </Text>
      <Text size="S" color="grayMedium">
        {t('label.bus_tracker_description')}
      </Text>
    </PurchaseCompleteBannerLayout>
  );
}

BusTrackerBanner.propTypes = {
  purchaseType: PropTypes.oneOf(['one_way', 'round_trip']),
  isOpenTicket: PropTypes.bool,
  className: PropTypes.string,
  imageColumnSize: PropTypes.oneOf(['compact', 'expanded']),
};

BusTrackerBanner.defaultProps = {
  purchaseType: 'one_way',
  isOpenTicket: false,
  className: '',
  imageColumnSize: 'compact',
};

export default BusTrackerBanner;
